/**
 * t413u: HR stage 3 on a phone (People › HR changes to approve, Starters and leavers, Missing HR details). Names open
 * the person sheet; the chevron opens their HR record (where Approve / Reject live). Payroll export, retention, erasure
 * and key rotation stay on a computer. The iOS app mirrors this screen for screen (apps/mobile/src/settings/hrOps.tsx).
 */
import { useState } from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { canRemindNow, hrDate, hrLeaverLine, hrRemindedLine, hrStarterLine, HR_REPORT_MONTHS, hrReportRangeMonths } from '@tradeoutlet/shared';
import { useHrMissing, useHrStartersLeavers } from '@tradeoutlet/data';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';
import { Spinner } from '@/components/ui';
import { refreshHr, useHrSettings } from '@/features/hr/hooks';
import { card, ghostBtn, head } from './PersonSettings';

function PersonRow({ id, name, lines, right, testId }: { id: string; name: string; lines: string[]; right?: React.ReactNode; testId: string }) {
  const nav = useNavigate();
  const openPerson = useUi(s => s.openPerson);
  return (
    <div className="flex min-h-[56px] items-center gap-2 border-b border-slate-100 py-2 last:border-0 dark:border-slate-800" data-testid={testId}>
      <div className="min-w-0 flex-1">
        <button type="button" className="text-left text-[15px] font-medium" onClick={() => openPerson(id)}>{name}</button>
        {lines.map(l => <div key={l} className="text-sm text-slate-500">{l}</div>)}
      </div>
      {right}
      <button type="button" aria-label={`Open ${name}’s personal details`} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full active:bg-slate-100 dark:active:bg-slate-800"
        onClick={() => nav(`/settings/people/users/${id}?tab=hr`)}><ChevronRight className="h-5 w-5 text-slate-400" aria-hidden /></button>
    </div>
  );
}

/** People › HR changes to approve. */
export function HrChangesPage() {
  const ws = useWorkspaceId();
  const q = useQuery({ queryKey: ['hr', ws ?? '', 'changes'], queryFn: () => api.hr.changes(ws!), enabled: Boolean(ws), retry: false });
  if (q.isLoading) return <div className="p-6"><Spinner /></div>;
  if (!q.data) return <p className="p-4 text-sm text-slate-500">{errorMessage(q.error)}</p>;
  const rows = q.data.filter(c => c.can_decide);
  return (
    <div className="flex-1 overflow-y-auto pb-6" data-testid="m-hr-changes">
      <div className={clsx(card, 'mt-3')}>
        {rows.map(c => <PersonRow key={c.id} id={c.user_id} name={c.user_name} lines={[`${c.label} · ${hrDate(c.requested_at.slice(0, 10))}`]} testId={`m-hr-change-${c.id}`} />)}
        {!rows.length && <p className="py-3 text-[15px] text-slate-500">Nothing to approve.</p>}
      </div>
    </div>
  );
}

/** People › Starters and leavers. */
export function HrReportsPage() {
  const ws = useWorkspaceId();
  const [months, setMonths] = useState<number>(3);
  const r = hrReportRangeMonths(months);
  const q = useHrStartersLeavers(ws, r.from, r.to);
  return (
    <div className="flex-1 overflow-y-auto pb-6" data-testid="m-hr-reports">
      <div className="px-4 pt-3">
        <div className="mb-1.5 text-xs font-semibold uppercase text-slate-600 dark:text-slate-400" aria-hidden>Period</div>
        <select aria-label="Period" value={months} onChange={e => setMonths(Number(e.target.value))} data-testid="m-hr-reports-period"
          className="h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-[15px] dark:border-slate-700 dark:bg-slate-900">
          {HR_REPORT_MONTHS.map(m => <option key={m} value={m}>{m === 1 ? 'Last month' : `Last ${m} months`}</option>)}
        </select>
      </div>
      {q.isLoading ? <div className="p-6"><Spinner /></div> : !q.data ? <p className="p-4 text-sm text-slate-500">{errorMessage(q.error)}</p> : <>
        <div className={head}>Starters</div>
        <div className={card}>
          {q.data.starters.map(s => <PersonRow key={s.user_id} id={s.user_id} name={s.display_name} lines={[hrStarterLine(s)]} testId={`m-hr-starter-${s.user_id}`} />)}
          {!q.data.starters.length && <p className="py-3 text-[15px] text-slate-500">No one started.</p>}
        </div>
        <div className={head}>Leavers</div>
        <div className={card}>
          {q.data.leavers.map(l => <PersonRow key={l.user_id} id={l.user_id} name={l.display_name} lines={[hrLeaverLine(l)]} testId={`m-hr-leaver-${l.user_id}`} />)}
          {!q.data.leavers.length && <p className="py-3 text-[15px] text-slate-500">No one left.</p>}
        </div>
        <p className="px-4 pt-2 text-xs text-slate-500">{hrDate(r.from)} to {hrDate(r.to)}</p>
      </>}
    </div>
  );
}

/** People › Missing HR details, with a reminder (bell and push, once a day at most). */
export function HrMissingPage() {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const st = useHrSettings().data;
  const q = useHrMissing(ws);
  const [busy, setBusy] = useState<string | null>(null);
  const remind = async (ids: string[], key: string) => {
    setBusy(key);
    try {
      const r = await api.hr.remind(ws!, ids);
      toast(r.sent ? `Reminder sent to ${r.sent === 1 ? '1 person' : `${r.sent} people`}` : 'Already reminded today', r.sent ? 'success' : 'info');
      void refreshHr(ws);
    } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(null); }
  };
  if (q.isLoading) return <div className="p-6"><Spinner /></div>;
  if (!q.data) return <p className="p-4 text-sm text-slate-500">{errorMessage(q.error)}</p>;
  const can = Boolean(st?.can_remind);
  const due = q.data.filter(r => canRemindNow(r.reminded_at));
  return (
    <div className="flex-1 overflow-y-auto pb-6" data-testid="m-hr-missing">
      <div className={clsx(card, 'mt-3')}>
        {q.data.map(r => (
          <PersonRow key={r.user_id} id={r.user_id} name={r.display_name} lines={[hrRemindedLine(r)]} testId={`m-hr-missing-${r.user_id}`}
            right={can && canRemindNow(r.reminded_at) ? <button type="button" className="h-9 shrink-0 rounded-lg px-3 text-sm font-medium active:bg-slate-100 dark:active:bg-slate-800" disabled={busy !== null}
              onClick={() => void remind([r.user_id], r.user_id)} data-testid={`m-hr-remind-${r.user_id}`}>{busy === r.user_id ? 'Sending…' : 'Remind'}</button> : null} />
        ))}
        {!q.data.length && <p className="py-3 text-[15px] text-slate-500">Everyone’s HR details are complete.</p>}
      </div>
      {can && due.length > 1 && <div className="px-3 pt-3"><button type="button" className={clsx(ghostBtn, 'w-full')} disabled={busy !== null}
        onClick={() => void remind(due.map(r => r.user_id), 'all')} data-testid="m-hr-remind-all">{busy === 'all' ? 'Sending…' : `Remind all ${due.length}`}</button></div>}
      <p className="px-4 pt-2 text-xs text-slate-500">Reminders go to the person’s bell and phone, once a day at most.</p>
    </div>
  );
}
